import { createTheme, alpha } from '@mui/material/styles';
import { useMemo } from 'react';

// Modern flat color palette inspired by contemporary design systems
const colors = {
  primary: {
    main: "#667EEA", // Modern purple-blue
    light: "#A5B4FC",
    dark: "#4C63D2",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#F093FB", // Vibrant pink-purple gradient feel
    light: "#FBB6FF",
    dark: "#E879F9",
    contrastText: "#FFFFFF",
  },
  accent: {
    main: "#4ECDC4", // Teal accent
    light: "#81E6DB",
    dark: "#26D0CE",
  },
  background: {
    default: "#FAFBFC", // Ultra-light neutral
    paper: "#FFFFFF",
    elevated: "#F8FAFC", // Slightly elevated surfaces
  },
  text: {
    primary: "#1A202C", // Rich dark for excellent readability
    secondary: "#4A5568", // Medium gray
    disabled: "#A0AEC0",
  },
  error: {
    main: "#F56565", // Modern red
    light: "#FEB2B2",
    dark: "#E53E3E",
  },
  warning: {
    main: "#ED8936", // Warm orange
    light: "#F6AD55",
    dark: "#DD6B20",
  },
  success: {
    main: "#48BB78", // Fresh green
    light: "#9AE6B4",
    dark: "#38A169",
  },
  info: {
    main: "#4299E1", // Sky blue
    light: "#90CDF4",
    dark: "#3182CE",
  },
  grey: {
    50: "#FAFBFC",
    100: "#F7FAFC",
    200: "#EDF2F7",
    300: "#E2E8F0",
    400: "#CBD5E0",
    500: "#A0AEC0",
    600: "#718096",
    700: "#4A5568",
    800: "#2D3748",
    900: "#1A202C",
  },
};

const baseTheme = {
  palette: {
    ...colors,
    mode: "light",
    divider: alpha(colors.grey[300], 0.6),
  },
  typography: {
    fontFamily: ["Inter", "SF Pro Display", "-apple-system", "BlinkMacSystemFont", '"Segoe UI"', "Roboto", "Arial", "sans-serif"].join(","),
    fontSize: 13, // Reduced base font size
    h1: {
      fontWeight: 700,
      letterSpacing: "-0.04em",
      fontSize: "2rem", // Reduced from 2.5rem
      lineHeight: 1.1,
      color: colors.text.primary,
    },
    h2: {
      fontWeight: 600,
      letterSpacing: "-0.03em",
      fontSize: "1.5rem", // Reduced from 2rem
      lineHeight: 1.2,
      color: colors.text.primary,
    },
    h3: {
      fontWeight: 600,
      letterSpacing: "-0.02em",
      fontSize: "1.25rem", // Reduced from 1.5rem
      lineHeight: 1.25,
      color: colors.text.primary,
    },
    h4: {
      fontWeight: 600,
      letterSpacing: "-0.01em",
      fontSize: "1.125rem", // Reduced from 1.25rem
      lineHeight: 1.3,
      color: colors.text.primary,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1rem", // Reduced from 1.125rem
      lineHeight: 1.4,
      color: colors.text.primary,
    },
    h6: {
      fontWeight: 600,
      fontSize: "0.875rem", // Reduced from 1rem
      lineHeight: 1.4,
      color: colors.text.primary,
    },
    subtitle1: {
      fontSize: "0.875rem", // Reduced from 1rem
      lineHeight: 1.5,
      fontWeight: 500,
      color: colors.text.secondary,
    },
    subtitle2: {
      fontSize: "0.8125rem", // Reduced from 0.875rem
      lineHeight: 1.5,
      fontWeight: 500,
      color: colors.text.secondary,
    },
    body1: {
      fontSize: "0.875rem", // Reduced from 1rem
      lineHeight: 1.6,
      color: colors.text.primary,
    },
    body2: {
      fontSize: "0.8125rem", // Reduced from 0.875rem
      lineHeight: 1.6,
      color: colors.text.secondary,
    },
    button: {
      fontWeight: 600,
      fontSize: "0.8125rem", // Reduced from 0.875rem
      textTransform: "none",
      letterSpacing: "0.01em",
    },
    caption: {
      fontSize: "0.6875rem", // Reduced from 0.75rem
      lineHeight: 1.5,
      color: colors.text.secondary,
    },
    overline: {
      fontSize: "0.75rem",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      color: colors.text.secondary,
    },
  },
  shape: {
    borderRadius: 6, // Subtle rounded corners
  },
  shadows: [
    "none",
    "0px 1px 2px rgba(0, 0, 0, 0.04)", // Ultra-subtle shadows for flat design
    "0px 1px 3px rgba(0, 0, 0, 0.06)",
    "0px 2px 4px rgba(0, 0, 0, 0.08)",
    "0px 2px 6px rgba(0, 0, 0, 0.08)",
    "0px 4px 8px rgba(0, 0, 0, 0.08)",
    "0px 4px 12px rgba(0, 0, 0, 0.08)",
    "0px 6px 16px rgba(0, 0, 0, 0.08)",
    "0px 8px 20px rgba(0, 0, 0, 0.08)",
    "0px 10px 24px rgba(0, 0, 0, 0.08)",
    "0px 12px 28px rgba(0, 0, 0, 0.08)",
    "0px 16px 32px rgba(0, 0, 0, 0.08)",
    "0px 20px 36px rgba(0, 0, 0, 0.08)",
    "0px 24px 40px rgba(0, 0, 0, 0.08)",
    "0px 28px 44px rgba(0, 0, 0, 0.08)",
    "0px 32px 48px rgba(0, 0, 0, 0.08)",
    "0px 36px 52px rgba(0, 0, 0, 0.08)",
    "0px 40px 56px rgba(0, 0, 0, 0.08)",
    "0px 44px 60px rgba(0, 0, 0, 0.08)",
    "0px 48px 64px rgba(0, 0, 0, 0.08)",
    "0px 52px 68px rgba(0, 0, 0, 0.08)",
    "0px 56px 72px rgba(0, 0, 0, 0.08)",
    "0px 60px 76px rgba(0, 0, 0, 0.08)",
    "0px 64px 80px rgba(0, 0, 0, 0.08)",
    "0px 68px 84px rgba(0, 0, 0, 0.08)",
  ],
  components: {
    // Modern flat buttons
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "4px", // Reduced from 6px
          padding: "6px 12px", // Reduced from 8px 16px
          fontSize: "0.8125rem", // Reduced from 0.875rem
          fontWeight: 600,
          boxShadow: "none",
          minHeight: "32px", // Reduced from 36px
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            boxShadow: "none",
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        },
        sizeSmall: {
          padding: "4px 8px",
          fontSize: "0.75rem",
          minHeight: "28px",
        },
        contained: {
          background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
          "&:hover": {
            background: `linear-gradient(135deg, ${colors.primary.dark} 0%, ${colors.primary.main} 100%)`,
          },
        },
        outlined: {
          border: `2px solid ${colors.primary.main}`,
          "&:hover": {
            border: `2px solid ${colors.primary.dark}`,
            backgroundColor: alpha(colors.primary.main, 0.04),
          },
        },
        sizeLarge: {
          padding: "10px 20px", // Reduced from 12px 24px
          fontSize: "0.9375rem", // Reduced from 1rem
          minHeight: "40px", // Reduced from 44px
          borderRadius: "6px", // Reduced from 8px
        },
        sizeSmall: {
          padding: "4px 8px", // Reduced from 6px 12px
          fontSize: "0.75rem", // Reduced from 0.8125rem
          minHeight: "28px", // Reduced from 32px
          borderRadius: "4px",
        },
      },
    },

    // Modern cards with subtle elevation
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          border: `1px solid ${alpha(colors.grey[300], 0.3)}`,
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.04)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.08)",
            transform: "translateY(-2px)",
          },
        },
      },
    },

    // Modern input fields
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            fontSize: "0.875rem",
            borderRadius: "6px",
          },
          "& .MuiOutlinedInput-root": {
            backgroundColor: colors.background.paper,
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "& fieldset": {
              borderColor: alpha(colors.grey[300], 0.5),
              borderWidth: "1px",
            },
            "&:hover fieldset": {
              borderColor: colors.primary.light,
            },
            "&.Mui-focused fieldset": {
              borderColor: colors.primary.main,
              borderWidth: "2px",
            },
          },
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem",
            color: colors.text.secondary,
            "&.Mui-focused": {
              color: colors.primary.main,
            },
          },
        },
      },
    },

    // Modern chips
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          fontSize: "0.8125rem",
          fontWeight: 500,
          height: "28px",
          backgroundColor: alpha(colors.primary.main, 0.1),
          color: colors.primary.dark,
          border: "none",
          "&:hover": {
            backgroundColor: alpha(colors.primary.main, 0.15),
          },
        },
        colorPrimary: {
          backgroundColor: colors.primary.main,
          color: colors.primary.contrastText,
          "&:hover": {
            backgroundColor: colors.primary.dark,
          },
        },
        colorSecondary: {
          backgroundColor: colors.secondary.main,
          color: colors.secondary.contrastText,
          "&:hover": {
            backgroundColor: colors.secondary.dark,
          },
        },
      },
    },

    // Modern dialogs
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "10px",
          boxShadow: "0px 24px 48px rgba(0, 0, 0, 0.12)",
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1.125rem",
          fontWeight: 700,
          padding: "20px 20px 12px",
          color: colors.text.primary,
        },
      },
    },

    // Modern tabs
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: "40px",
        },
        indicator: {
          height: "2px",
          borderRadius: "2px",
          backgroundColor: colors.primary.main,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          padding: "8px 16px",
          minHeight: "40px",
          fontSize: "0.875rem",
          fontWeight: 500,
          textTransform: "none",
          color: colors.text.secondary,
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "&.Mui-selected": {
            color: colors.primary.main,
            fontWeight: 600,
          },
          "&:hover": {
            color: colors.primary.main,
          },
        },
      },
    },

    // Modern alerts
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          padding: "10px 12px",
          border: "none",
        },
        standardSuccess: {
          backgroundColor: alpha(colors.success.main, 0.1),
          color: colors.success.dark,
        },
        standardError: {
          backgroundColor: alpha(colors.error.main, 0.1),
          color: colors.error.dark,
        },
        standardWarning: {
          backgroundColor: alpha(colors.warning.main, 0.1),
          color: colors.warning.dark,
        },
        standardInfo: {
          backgroundColor: alpha(colors.info.main, 0.1),
          color: colors.info.dark,
        },
      },
    },

    // Modern paper surfaces
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          backgroundColor: colors.background.paper,
        },
        elevation1: {
          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.06)",
          border: `1px solid ${alpha(colors.grey[300], 0.2)}`,
        },
        elevation2: {
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
          border: `1px solid ${alpha(colors.grey[300], 0.15)}`,
        },
        elevation3: {
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.08)",
          border: `1px solid ${alpha(colors.grey[300], 0.1)}`,
        },
      },
    },

    // Modern list items
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          margin: "1px 0",
          padding: "8px 12px",
          "&:hover": {
            backgroundColor: alpha(colors.primary.main, 0.04),
          },
        },
      },
    },

    // Modern menu items
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: "8px 12px",
          fontSize: "0.875rem",
          borderRadius: "4px",
          margin: "1px 6px",
          "&:hover": {
            backgroundColor: alpha(colors.primary.main, 0.08),
          },
          "&.Mui-selected": {
            backgroundColor: alpha(colors.primary.main, 0.12),
            "&:hover": {
              backgroundColor: alpha(colors.primary.main, 0.16),
            },
          },
        },
      },
    },

    // Modern table
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: alpha(colors.grey[300], 0.3),
          padding: "12px 16px",
          fontSize: "0.875rem",
        },
        head: {
          fontWeight: 600,
          backgroundColor: colors.background.elevated,
          color: colors.text.primary,
        },
      },
    },

    // Modern accordion
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: "8px !important",
          border: `1px solid ${alpha(colors.grey[300], 0.3)}`,
          boxShadow: "none",
          "&:before": {
            display: "none",
          },
          "&.Mui-expanded": {
            margin: "6px 0",
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: "12px 16px",
          "&.Mui-expanded": {
            borderBottom: `1px solid ${alpha(colors.grey[300], 0.3)}`,
          },
        },
      },
    },
  },
};

export function useAppTheme() {
  return useMemo(() => createTheme(baseTheme), []);
}

export default createTheme(baseTheme);